import { useCallback, useEffect } from "react";
import { useFeatureFlag } from "Hooks/useFeatureFlag";
import { IIfcArtifacts, hasReadyIfcArtifacts } from "Data/interfaces/Viewer/IIfcArtifacts";
import { isWebGPUAvailable } from "Utils/webgpu";

export const extensions2dAnd3d = [
  'dwg', 'dxf', 'dgn', 'rvt', 'nwc', 'nwd',
  'ifc', 'dwf', 'dwfx', 'rcp',
];

export const extensions3d = [
  'rvt', 'dwf', 'nwc', 'nwd', 'ifc', 'dwfx',
  'rcp', 'skp',
];

export const allOtherExtensions = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'doc',
  'docx', 'docm', 'ppt', 'pps', 'ppsx', 'ppsm',
  'pptx', 'pptm', 'xls', 'xlsx', 'xlsm', 'rtf',
  'pdf', 'txt', 'html', 'gdoc', 'gsheet', 'gslides',
];

export const extensionsImage = [
  'jpg', 'jpeg', 'png', 'tif', 'bmp', 'webp',
  'gif',
];

export const extensionViews2d3d = ['rvt', 'dwf', 'dwfx', 'ifc', 'rcp', 'skp'];

export const extensionPranchas = ['rvt', 'dwf', 'dwfx'];

export const extensionViewpoints = ['nwc', 'nwd'];

export const COORDLY_2D_VIEWER_EXTENSIONS = ['dwg', 'dxf'];
export const COORDLY_3D_VIEWER_EXTENSIONS = ['ifc', 'gltf', 'glb', 'obj', 'stl'];

export const useCoordlyViewerFeature = () => {
  const coordlyViewerFeatureFlag = useFeatureFlag('coordly-2d-viewer');
  const isCoordlyViewerEnabled = coordlyViewerFeatureFlag.enabled && coordlyViewerFeatureFlag.value === 'test';

  const isCoordly2DViewerEligible = (fileExtension: string) => {
    return isCoordlyViewerEnabled && COORDLY_2D_VIEWER_EXTENSIONS.indexOf(fileExtension.toLocaleLowerCase()) !== -1;
  }

  // O 3D saiu daqui: tem flag própria (`coordly-3d-viewer`) e gate próprio.
  // Fonte única = useCoordly3DViewerFeature.
  const getCoordlyViewerType = (fileExtension: string): '2D' | null => {
    return isCoordly2DViewerEligible(fileExtension) ? '2D' : null;
  }

  return {
    getCoordlyViewerType,
    isCoordly2DViewerEligible,
  }
};

export const COORDLY_3D_VIEWER_FLAG = 'coordly-3d-viewer';

// Nesta fase o gate 3D vale só para IFC; as demais extensões de
// COORDLY_3D_VIEWER_EXTENSIONS são backlog do motor.
export const COORDLY_3D_VIEWER_EXTENSION = 'ifc';

export type Coordly3DFallbackReason =
  | 'not-ifc'
  | 'no-webgpu'
  | 'flag-off'
  | 'artifacts-missing';

export type Coordly3DDecision =
  | { target: 'server' }
  | { target: 'autodesk'; reason: Coordly3DFallbackReason };

export interface ICoordly3DInput {
  extension?: string;
  artifacts?: IIfcArtifacts | null;
}

/**
 * O parse é sempre no server. Dois caminhos, nesta ordem:
 *  1. artefatos prontos no CDN (`ifcArtifacts`), quando o backend .NET existir;
 *  2. parse sob demanda no server ifc-lite (`REACT_APP_IFC_SERVER_URL`) — o browser
 *     manda o .ifc e recebe a geometria tesselada.
 *
 * O (2) é o que sustenta o gate hoje. O parse client-side foi descartado: quebra
 * arquivos (o single-thread do motor perde geometria) e o paralelo exigiria isolar
 * a origem, o que derruba a tela de Painéis. Sem artefatos E sem server, Autodesk.
 */
export const decideCoordly3DTarget = (
  isEnabled: boolean,
  hasWebGPU: boolean,
  input: ICoordly3DInput,
): Coordly3DDecision => {
  if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
    return { target: 'autodesk', reason: 'not-ifc' };
  }
  if (!hasWebGPU) { return { target: 'autodesk', reason: 'no-webgpu' }; }
  if (!isEnabled) { return { target: 'autodesk', reason: 'flag-off' }; }

  return (hasReadyIfcArtifacts(input.artifacts) || !!process.env.REACT_APP_IFC_SERVER_URL)
    ? { target: 'server' }
    : { target: 'autodesk', reason: 'artifacts-missing' };
};

export const useCoordly3DViewerFeature = () => {
  const flag = useFeatureFlag(COORDLY_3D_VIEWER_FLAG);
  const isCoordly3DViewerEnabled = !!flag?.enabled && flag?.value === 'test';

  // Aquece o probe: a decisão de abertura precisa dele resolvido ANTES de
  // navegar, e requestAdapter() é async.
  useEffect(() => {
    if (isCoordly3DViewerEnabled) { void isWebGPUAvailable(); }
  }, [isCoordly3DViewerEnabled]);

  // Ordem do gate: ifc → WebGPU → flag → artefatos.
  const decideCoordly3D = useCallback(async (input: ICoordly3DInput): Promise<Coordly3DDecision> => {
    if ((input.extension || '').toLocaleLowerCase() !== COORDLY_3D_VIEWER_EXTENSION) {
      return { target: 'autodesk', reason: 'not-ifc' };
    }
    return decideCoordly3DTarget(isCoordly3DViewerEnabled, await isWebGPUAvailable(), input);
  }, [isCoordly3DViewerEnabled]);

  return { isCoordly3DViewerEnabled, decideCoordly3D };
};

/**
 * Extensões que o federado aceita incluir. Com a flag do viewer 3D ativa, só IFC:
 * o conversor automático da Autodesk está pausado até segunda ordem, então
 * qualquer outra extensão entraria como modelo que ninguém consegue renderizar.
 */
export const useFederatedUploadFilter = () => {
  const { isCoordly3DViewerEnabled } = useCoordly3DViewerFeature();

  const isUploadableExtension = useCallback((extension?: string) => (
    isCoordly3DViewerEnabled
      ? (extension || '').toLocaleLowerCase() === COORDLY_3D_VIEWER_EXTENSION
      : isValid3DExtension(extension || '')
  ), [isCoordly3DViewerEnabled]);

  return { isUploadableExtension, onlyIfc: isCoordly3DViewerEnabled };
};

export const isValid3DExtension = (extension: string) => {
  if (extensions3d.indexOf(extension.toLocaleLowerCase()) !== -1) {
    return true;
  }
  return false;
}

export const getFileExtension = (filename: string) => {
  var ext = /^.+\.([^.]+)$/.exec(filename);
  return ext == null ? "" : ext[1];
}
